import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, isPast } from 'date-fns';
import {
  ClipboardList,
  Plus,
  Search,
  Clock,
  AlertTriangle,
  CheckCircle,
  Wrench,
  User,
  MapPin,
  MoreVertical,
  Play,
  Pause,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  workOrdersApi,
  WorkOrder,
  WorkOrderDashboard,
  WORK_ORDER_TYPES,
  WORK_ORDER_PRIORITIES,
  WORK_ORDER_STATUSES,
} from '@/api/services/work-orders.api';
import { equipmentApi, Equipment } from '@/api/services/equipment.api';
import { locationsApi, Location } from '@/api/services/locations.api';

const priorityColors: Record<string, string> = {
  LOW: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  MEDIUM: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  HIGH: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  URGENT: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  EMERGENCY: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

const statusColors: Record<string, string> = {
  DRAFT: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  PENDING_APPROVAL: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  APPROVED: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  SCHEDULED: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  IN_PROGRESS: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  ON_HOLD: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  COMPLETED: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  CANCELLED: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  CLOSED: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
};

export default function WorkOrders() {
  const navigate = useNavigate();
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [dashboard, setDashboard] = useState<WorkOrderDashboard | null>(null);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Form state
  const [formData, setFormData] = useState({
    type: 'CORRECTIVE',
    priority: 'MEDIUM',
    title: '',
    description: '',
    equipmentId: '',
    locationId: '',
    scheduledStart: '',
    scheduledEnd: '',
    estimatedHours: '',
  });

  useEffect(() => {
    loadData();
  }, [page, search, statusFilter, priorityFilter, typeFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params: any = { page, limit: 20 };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (typeFilter) params.type = typeFilter;

      const [workOrdersRes, dashboardRes, equipmentRes, locationsRes] = await Promise.all([
        workOrdersApi.getAll(params),
        workOrdersApi.getDashboard(),
        equipmentApi.getAll({ limit: 100 }),
        locationsApi.getAll({ limit: 100 }),
      ]);

      setWorkOrders(workOrdersRes.data.data);
      setTotalPages(workOrdersRes.data.pagination.totalPages);
      setDashboard(dashboardRes.data);
      setEquipment(equipmentRes.data.data);
      setLocations(locationsRes.data);
    } catch (error) {
      toast.error('Failed to load work orders');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const data: any = {
        type: formData.type,
        priority: formData.priority,
        title: formData.title,
        locationId: formData.locationId,
      };

      if (formData.description) data.description = formData.description;
      if (formData.equipmentId) data.equipmentId = formData.equipmentId;
      if (formData.scheduledStart) data.scheduledStart = formData.scheduledStart;
      if (formData.scheduledEnd) data.scheduledEnd = formData.scheduledEnd;
      if (formData.estimatedHours) data.estimatedHours = parseFloat(formData.estimatedHours);

      const result = await workOrdersApi.create(data);
      toast.success(`Work order ${result.data.workOrderNumber} created`);
      setIsCreateOpen(false);
      resetForm();
      navigate(`/work-orders/${result.data.id}`);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create work order');
    }
  };

  const handleStatusUpdate = async (wo: WorkOrder, newStatus: string) => {
    try {
      await workOrdersApi.updateStatus(wo.id, { status: newStatus });
      toast.success('Work order status updated');
      loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update status');
    }
  };

  const resetForm = () => {
    setFormData({
      type: 'CORRECTIVE',
      priority: 'MEDIUM',
      title: '',
      description: '',
      equipmentId: '',
      locationId: '',
      scheduledStart: '',
      scheduledEnd: '',
      estimatedHours: '',
    });
  };

  const getOpenCount = () => {
    return (
      (dashboard?.byStatus?.DRAFT || 0) +
      (dashboard?.byStatus?.PENDING_APPROVAL || 0) +
      (dashboard?.byStatus?.APPROVED || 0) +
      (dashboard?.byStatus?.SCHEDULED || 0) +
      (dashboard?.byStatus?.IN_PROGRESS || 0) +
      (dashboard?.byStatus?.ON_HOLD || 0)
    );
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Work Orders</h1>
          <p className="text-sm text-muted-foreground">
            Manage maintenance and repair work orders
          </p>
        </div>
        <Button onClick={() => setIsCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New Work Order
        </Button>
      </div>

      {/* Dashboard Cards */}
      {dashboard && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Open Work Orders</CardTitle>
              <ClipboardList className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{getOpenCount()}</div>
              <p className="text-xs text-muted-foreground">
                {dashboard.byStatus?.IN_PROGRESS || 0} in progress
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Overdue</CardTitle>
              <AlertTriangle className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{dashboard.overdue}</div>
              <p className="text-xs text-muted-foreground">past scheduled end date</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Completed This Month</CardTitle>
              <CheckCircle className="h-4 w-4 text-emerald-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-emerald-600">
                {dashboard.completedThisMonth}
              </div>
              <p className="text-xs text-muted-foreground">work orders completed</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Avg. Completion Time</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {dashboard.avgCompletionTime ? `${dashboard.avgCompletionTime}h` : '-'}
              </div>
              <p className="text-xs text-muted-foreground">this month</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search work orders..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {WORK_ORDER_STATUSES.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={(v) => setPriorityFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            {WORK_ORDER_PRIORITIES.map((p) => (
              <SelectItem key={p.value} value={p.value}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {WORK_ORDER_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Work Orders Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Work Order</TableHead>
              <TableHead>Equipment</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Assignee</TableHead>
              <TableHead>Scheduled</TableHead>
              <TableHead>Priority</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[80px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8">
                  <div className="flex items-center justify-center">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  </div>
                </TableCell>
              </TableRow>
            ) : workOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  No work orders found
                </TableCell>
              </TableRow>
            ) : (
              workOrders.map((wo) => (
                <TableRow
                  key={wo.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(`/work-orders/${wo.id}`)}
                >
                  <TableCell>
                    <div>
                      <div className="font-medium">{wo.workOrderNumber}</div>
                      <div className="text-sm text-muted-foreground">{wo.title}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {wo.equipment ? (
                      <div className="flex items-center gap-2">
                        <Wrench className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="text-sm">{wo.equipment.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {wo.equipment.assetNumber}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{wo.location?.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {wo.assignedTo ? (
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{wo.assignedTo.fullName}</span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm">Unassigned</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {wo.scheduledStart ? (
                      <div className="space-y-1">
                        <div className="text-sm">
                          {format(new Date(wo.scheduledStart), 'MMM d, yyyy')}
                        </div>
                        {wo.scheduledEnd && isPast(new Date(wo.scheduledEnd)) &&
                          !['COMPLETED', 'CANCELLED', 'CLOSED'].includes(wo.status) && (
                          <Badge variant="outline" className="text-red-500 border-red-200">
                            Overdue
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm">Not scheduled</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={priorityColors[wo.priority]}>{wo.priority}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={statusColors[wo.status]}>
                      {WORK_ORDER_STATUSES.find((s) => s.value === wo.status)?.label || wo.status}
                    </Badge>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {wo.status === 'DRAFT' && (
                          <DropdownMenuItem
                            onClick={() => handleStatusUpdate(wo, 'PENDING_APPROVAL')}
                          >
                            Submit for Approval
                          </DropdownMenuItem>
                        )}
                        {wo.status === 'PENDING_APPROVAL' && (
                          <DropdownMenuItem onClick={() => handleStatusUpdate(wo, 'APPROVED')}>
                            Approve
                          </DropdownMenuItem>
                        )}
                        {wo.status === 'APPROVED' && (
                          <>
                            <DropdownMenuItem onClick={() => handleStatusUpdate(wo, 'SCHEDULED')}>
                              Schedule
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleStatusUpdate(wo, 'IN_PROGRESS')}
                            >
                              <Play className="mr-2 h-4 w-4" />
                              Start Work
                            </DropdownMenuItem>
                          </>
                        )}
                        {wo.status === 'SCHEDULED' && (
                          <DropdownMenuItem onClick={() => handleStatusUpdate(wo, 'IN_PROGRESS')}>
                            <Play className="mr-2 h-4 w-4" />
                            Start Work
                          </DropdownMenuItem>
                        )}
                        {wo.status === 'IN_PROGRESS' && (
                          <>
                            <DropdownMenuItem onClick={() => handleStatusUpdate(wo, 'ON_HOLD')}>
                              <Pause className="mr-2 h-4 w-4" />
                              Put On Hold
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/work-orders/${wo.id}`)}>
                              <CheckCircle className="mr-2 h-4 w-4" />
                              Complete
                            </DropdownMenuItem>
                          </>
                        )}
                        {wo.status === 'ON_HOLD' && (
                          <DropdownMenuItem onClick={() => handleStatusUpdate(wo, 'IN_PROGRESS')}>
                            <Play className="mr-2 h-4 w-4" />
                            Resume
                          </DropdownMenuItem>
                        )}
                        {wo.status === 'COMPLETED' && (
                          <DropdownMenuItem onClick={() => handleStatusUpdate(wo, 'CLOSED')}>
                            Close Work Order
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        {!['COMPLETED', 'CANCELLED', 'CLOSED'].includes(wo.status) && (
                          <DropdownMenuItem
                            onClick={() => handleStatusUpdate(wo, 'CANCELLED')}
                            className="text-red-600"
                          >
                            <XCircle className="mr-2 h-4 w-4" />
                            Cancel
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
          </Button>
        </div>
      )}

      {/* Create Work Order Sheet */}
      <Sheet open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Create Work Order</SheetTitle>
            <SheetDescription>Create a new work order for maintenance or repair</SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                placeholder="e.g., Replace hydraulic pump"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="type">Type</Label>
                <Select
                  value={formData.type}
                  onValueChange={(v) => setFormData({ ...formData, type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WORK_ORDER_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(v) => setFormData({ ...formData, priority: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WORK_ORDER_PRIORITIES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Select
                value={formData.locationId}
                onValueChange={(v) => setFormData({ ...formData, locationId: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name} ({loc.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="equipment">Equipment (Optional)</Label>
              <Select
                value={formData.equipmentId}
                onValueChange={(v) => setFormData({ ...formData, equipmentId: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select equipment" />
                </SelectTrigger>
                <SelectContent>
                  {equipment.map((eq) => (
                    <SelectItem key={eq.id} value={eq.id}>
                      {eq.name} ({eq.assetNumber})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="scheduledStart">Scheduled Start</Label>
                <Input
                  id="scheduledStart"
                  type="datetime-local"
                  value={formData.scheduledStart}
                  onChange={(e) => setFormData({ ...formData, scheduledStart: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="scheduledEnd">Scheduled End</Label>
                <Input
                  id="scheduledEnd"
                  type="datetime-local"
                  value={formData.scheduledEnd}
                  onChange={(e) => setFormData({ ...formData, scheduledEnd: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="estimatedHours">Estimated Hours</Label>
              <Input
                id="estimatedHours"
                type="number"
                step="0.5"
                placeholder="4.0"
                value={formData.estimatedHours}
                onChange={(e) => setFormData({ ...formData, estimatedHours: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Detailed description of the work required..."
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={!formData.title || !formData.locationId}>
                Create Work Order
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
